/**
 * As contas do sino e da página de Notificações.
 *
 * Fica fora do componente pelo mesmo motivo de `cabecalho.js`: é conta, não
 * pintura. Data que o servidor manda em dois formatos (`2026-08-21` do prazo,
 * `2026-08-21 14:30:00` da mensagem), a faixa de gravidade de cada aviso e o
 * filtro por origem são exatamente o tipo de coisa que quebra em silêncio na
 * tela — aqui tem teste (`avisos.test.jsx`).
 */
import { emData } from "./visual.jsx";

/** As origens que o servidor manda em `tipo`, na ordem em que a tela mostra. */
export const TIPOS_DE_AVISO = [
  { chave: "todos", rotulo: "Tudo" },
  { chave: "prazo", rotulo: "Prazos" },
  { chave: "sla", rotulo: "Sem resposta" },
  { chave: "copiloto", rotulo: "Assistente" },
];

/**
 * A data como ela vem do SQLite, virada em `Date`.
 *
 * `2026-08-21 14:30:00` sem fuso é UTC no banco; `new Date()` desse texto puro
 * dá "Invalid Date" no Safari. Prazo é dia seco (`2026-08-21`) e vale meio-dia
 * local — marcar meia-noite UTC fazia o prazo de hoje aparecer como ontem para
 * quem está em Brasília.
 */
export const dataDoAviso = (quando) => emData(quando);

const DIA = 86_400_000;
const soODia = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/**
 * As faixas de GRAVIDADE, na ordem em que a página as mostra.
 *
 * A página era agrupada por DIA, e isso a fazia uma Agenda pior: a Agenda é
 * dona do eixo do tempo (`vistas/Agenda.jsx`), e lá o dia é a pergunta. Aqui a
 * pergunta é outra — "o que exige ação de mim agora, e quão ruim é" —, e quem
 * responde é a gravidade. Com o dia como título, o prazo VENCIDO ia para o pé
 * da página (a data mais antiga) e o que vence em três dias abria a lista.
 *
 * `info` é o balde do que NÃO exige ação: o que o assistente mexeu é para
 * conferir, não para correr atrás. Misturado com prazo, empatava com ele.
 */
export const BANDAS = [
  { chave: "atrasado", rotulo: "Atrasado", tom: "var(--color-terra-escura)" },
  { chave: "hoje", rotulo: "Precisa de hoje", tom: "var(--color-ambar-escuro)" },
  { chave: "proximo", rotulo: "Nos próximos dias", tom: "var(--color-tinta-suave)" },
  { chave: "info", rotulo: "Só para saber", tom: "var(--color-verde-fundo)" },
];

/** "há 40 min", "há 3 h", "há 2 dias" — o tempo desde que aconteceu. */
function faz(d, agora) {
  const min = Math.max(0, Math.round((agora.getTime() - d.getTime()) / 60_000));
  if (min < 60) return `há ${min} min`;
  if (min < 1440) return `há ${Math.round(min / 60)} h`;
  const dias = Math.round(min / 1440);
  return `há ${dias} ${dias === 1 ? "dia" : "dias"}`;
}

/**
 * A gravidade de um aviso: em que faixa ele cai e a frase que a linha mostra.
 *
 * A frase é em PORTUGUÊS e não em data — "venceu há 3 dias" no lugar de
 * "07 de jul.". O título do prazo já dizia "Prazo em 0 dia(s)", o selo da
 * direita repetia a data e o cabeçalho do dia repetia os dois: três lugares
 * falando de tempo, e nenhum dizendo de quem era o aviso.
 */
export function urgencia(aviso, agora = new Date()) {
  const d = dataDoAviso(aviso?.quando);
  if (!d) return { banda: "info", frase: "sem data" };
  if (aviso.tipo === "prazo") {
    const dias = Math.round((soODia(d) - soODia(agora)) / DIA);
    if (dias < 0) return { banda: "atrasado", frase: `venceu ${faz(new Date(soODia(d)), new Date(soODia(agora)))}` };
    if (dias === 0) return { banda: "hoje", frase: "vence hoje" };
    if (dias === 1) return { banda: "proximo", frase: "vence amanhã" };
    return { banda: "proximo", frase: `vence em ${dias} dias` };
  }
  /* Cliente esperando é sempre de hoje: a janela do servidor é de três dias e
     quem espera desde ontem é o mais urgente da faixa, não o menos. */
  if (aviso.tipo === "sla") return { banda: "hoje", frase: `espera ${faz(d, agora)}` };
  return { banda: "info", frase: faz(d, agora) };
}

/** Só de uma origem, e só o que casa com a busca. `todos`/vazio não filtra. */
export function filtrarAvisos(avisos, tipo = "todos", termo = "") {
  const t = termo.trim().toLowerCase();
  return (avisos ?? []).filter(
    (a) =>
      (tipo === "todos" || !tipo || a.tipo === tipo) &&
      /* O NOME DO CLIENTE entra na busca: é o que a pessoa digita. Sem ele,
         procurar "Angélica" não achava o prazo da Angélica — o nome saiu de
         `corpo` para campo próprio quando ele virou a manchete da linha. */
      (!t || `${a.cliente ?? ""} ${a.titulo ?? ""} ${a.corpo ?? ""}`.toLowerCase().includes(t)),
  );
}

/** Quantos de cada origem — é o número das pastilhas, contado uma vez só. */
export function contarPorTipo(avisos) {
  const contas = { todos: (avisos ?? []).length };
  for (const a of avisos ?? []) contas[a.tipo] = (contas[a.tipo] ?? 0) + 1;
  return contas;
}

/**
 * Em faixas de gravidade, na ordem de `BANDAS`, e cada faixa por urgência.
 *
 * Faixa vazia não desenha: banda "Atrasado" com zero linhas é a página gritando
 * sobre o que não existe.
 */
export function porBanda(avisos, agora = new Date()) {
  const cestos = new Map(BANDAS.map((b) => [b.chave, []]));
  for (const a of avisos ?? []) cestos.get(urgencia(a, agora).banda)?.push(a);
  return BANDAS.filter((b) => cestos.get(b.chave).length > 0).map((b) => ({
    ...b,
    /* Dentro da faixa, o mais antigo primeiro: é o prazo que venceu há mais
       tempo e o cliente que espera desde ontem. */
    itens: cestos.get(b.chave).toSorted((x, y) => (x.quando > y.quando ? 1 : -1)),
  }));
}
